import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import FooterApercu from "../components/FooterApercu";
import { SocieteContext } from "../context/SocieteContext";
import TableauStyleVertical from "../components/TableauStyleVertical";
import "../index.css";
import BlocTotaux from "../components/BlocTotaux";
import html2pdf from "html2pdf.js";
import DesignationPickerModal from "../components/DesignationPickerModal";
import DOMPurify from "dompurify";
import { hydrateProtectedHtml } from "../utils/protectedFiles";
import { createPortal } from "react-dom";
import { notify } from "../ui/notify";
// === WYSIWYG (mêmes options que Mentions Légales)
import ReactQuill from "react-quill";
import Quill from "quill";
import "react-quill/dist/quill.snow.css";
import BoutonOnglet from "../components/BoutonOnglet";
import SkyTab from "../components/SkyTab";
import { loadTableTheme, themeStyleVars, onThemeChange } from "../utils/tableTheme";
import UnsavedDialog from "../components/UnsavedDialog";

function useUnsavedChanges({ isDirty, onSave, navigate, askConfirm }) {
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const confirmLeave = useCallback(async (targetHrefOrFn) => {
    if (!isDirty) {
      if (typeof targetHrefOrFn === "function") return targetHrefOrFn();
      if (typeof targetHrefOrFn === "string") return (window.location.href = targetHrefOrFn);
      return;
    }
    // ouvre la popup pro (le composant fournit askConfirm)
    const action = askConfirm ? await askConfirm() : "cancel";
    /*
      action ∈ "leave" | "save" | "cancel"
      - "leave" : quitter sans enregistrer
      - "save"  : enregistrer puis quitter
      - "cancel": rester sur la page
    */
    if (action === "leave") {
      if (typeof targetHrefOrFn === "function") return targetHrefOrFn();
      if (typeof targetHrefOrFn === "string") return (window.location.href = targetHrefOrFn);
      return;
    }
    if (action === "save" && typeof onSave === "function") {
      try {
        await onSave();
        if (typeof targetHrefOrFn === "function") return targetHrefOrFn();
        if (typeof targetHrefOrFn === "string") return (window.location.href = targetHrefOrFn);
      } catch (e) {
        console.warn("Échec enregistrement avant de quitter:", e);
      }
    }
    // "cancel" -> ne rien faire

  }, [isDirty, onSave, askConfirm]);

  useEffect(() => {
    const GUARD_FLAG = "__GUARD__";
    const initial = { ...(history.state || {}), [GUARD_FLAG]: true };
    try { history.replaceState(initial, ""); } catch { }
    try { history.pushState({ ...initial }, ""); } catch { }

    const onPop = () => {
      if (!isDirty) return;
      try { history.pushState({ ...(history.state || {}), [GUARD_FLAG]: true }, ""); } catch { }
      confirmLeave(() => history.back());
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [isDirty, confirmLeave]);

  useEffect(() => {
    const onClick = (e) => {
      if (!isDirty) return;
      let el = e.target;
      while (el && el !== document.body) {
        if (el.tagName === "A" && el.href) break;
        el = el.parentElement;
      }
      if (!el || el.tagName !== "A") return;

      const href = el.getAttribute("href");
      const target = el.getAttribute("target");
      if (!href || href.startsWith("#") || target === "_blank") return;

      const url = new URL(el.href);
      if (url.origin !== window.location.origin) return;

      e.preventDefault();
      e.stopPropagation();
      confirmLeave(() => {
        if (navigate) return navigate(url.pathname + url.search + url.hash);
        window.location.href = el.href;
      });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [isDirty, confirmLeave, navigate]);

  const safeNavigate = useCallback((to) => {
    confirmLeave(() => (navigate ? navigate(to) : (window.location.href = to)));
  }, [confirmLeave, navigate]);

  return { safeNavigate, confirmLeave };
}
// --- Helper pour afficher un message d'erreur lisible
function extractErr(e) {
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    (Array.isArray(e?.response?.data?.errors) ? e.response.data.errors.join(", ") : null) ||
    e?.message ||
    "Erreur inconnue"
  );
}
function applyActionsFront(lignes, actions = []) {
  const arr = Array.isArray(lignes) ? [...lignes] : [];
  for (const a of actions) {
    if (!a || !a.op) continue;

    if (a.op === "append") {
      arr.push(a.row);
    } else if (a.op === "insert") {
      const idx = Math.max(0, Math.min(arr.length, Number(a.index ?? arr.length)));
      arr.splice(idx, 0, a.row);
    } else if (a.op === "update") {
      const idx = Number(a.index);
      if (Number.isFinite(idx) && arr[idx]) arr[idx] = { ...arr[idx], ...a.patch };
    } else if (a.op === "delete") {
      const idx = Number(a.index);
      if (Number.isFinite(idx) && idx >= 0 && idx < arr.length) arr.splice(idx, 1);
    } else if (a.op === "move") {
      const from = Number(a.from), to = Number(a.to);
      if (Number.isFinite(from) && Number.isFinite(to) && arr[from]) {
        const [it] = arr.splice(from, 1);
        arr.splice(Math.max(0, Math.min(arr.length, to)), 0, it);
      }
    }
  }
  return arr;
}

// Attributor 'size' robuste (évite double registre en StrictMode)
try {
  const SizeStyle = Quill.import("attributors/style/size");
  SizeStyle.whitelist = ["12px", "14px", "16px", "18px", "24px", "32px"];
  // Empêche la double registration en dev/StrictMode
  if (!window.__PL_SIZE_REGISTERED__) {
    Quill.register(SizeStyle, true);
    window.__PL_SIZE_REGISTERED__ = true;
  }
} catch (e) {
  console.warn("Quill size attributor non initialisé :", e);
}

// Lis le tenant comme dans axiosInstance
function readTenantId() {
  const fromLS = localStorage.getItem("tenant_id");
  if (fromLS && String(fromLS).trim()) return String(fromLS).trim();
  if (typeof window !== "undefined" && window.__TENANT_ID__) return String(window.__TENANT_ID__);
  try {
    const m = document.cookie.match(/(?:^|;\s*)tenant_id=([^;]+)/);
    if (m) return decodeURIComponent(m[1]);
  } catch { }
  return null;
}


// Helpers format/num
const fmt2 = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));
const toCurrency = (v) => fmt2(v) + " €";
const fmtPct = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(Number(n || 0));
const round2 = (n) => Number((Number(n || 0)).toFixed(2));
const normalizeTva = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(20, Number(n.toFixed(2))));
};
const isProbablyHtml = (s = "") => /<\/?[a-z][\s\S]*>/i.test(s);
const ensureHtml = (v = "") => (isProbablyHtml(v) ? v :
  (v || "").trim().split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("") || "<p></p>");
const htmlToText = (html = "") => {
  let s = String(html || "");
  s = s.replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p>/gi, "\n\n")
    .replace(/<\/?p[^>]*>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return s.trim();
};

/* ---------------- Helpers ---------------- */
const textToHtml = (txt = "") => {
  const esc = (t) =>
    t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const blocks = String(txt || "")
    .trim()
    .split(/\n{2,}/)
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br/>")}</p>`);
  return blocks.join("") || "<p></p>";
};



async function persistDesignationsDepuisLignes(lignes, { societeId, projectName, tvaParDefaut = 20 }) {
  for (const l of (lignes || [])) {
    if (!l || l.type === "note" || l.type === "pagebreak") continue;
    const label = String(l.designation || "").trim();
    if (!label) continue;

    const payload = {
      // back A
      label: label,
      prixUnitaire: Number(l.prixUnitaire || 0),
      tva: (l.tva === 0 || Number.isFinite(Number(l.tva))) ? Number(l.tva) : Number(tvaParDefaut),

      // back B (compat si ton API accepte "titre/prix")
      titre: label,
      prix: Number(l.prixUnitaire || 0),

      // scope / unicité côté back
      societe_id: societeId,
      project: projectName,
    };

    // si c’est du texte simple, tu peux décider d’envoyer la version HTML :
    // payload.label = ensureHtml(label); payload.titre = payload.label;

    try {
      await axiosInstance.post("/designations", payload);
    } catch (e) {
      // 409 = déjà existant (unicité), on ignore
      if (e?.response?.status !== 409) {
        console.error("Persist désignation échouée:", e);
      }
    }
  }
}

const clampPct = (n) => Math.max(0, Math.min(100, Number(n || 0)));

const formatNumeroDevis = (n) =>
  !n ? "—" : /^\d+$/.test(String(n)) ? `DEV-${String(n).padStart(4, "0")}` : String(n);

const toLocalISO = (date = new Date()) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
function formatDateFR(raw) {
  if (!raw) return "-";
  if (typeof raw === "string") {
    const s = raw.slice(0, 10);
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
  }
  const d = new Date(raw);
  return `${String(d.getDate()).padStart(2, "0")}/${String(
    d.getMonth() + 1
  ).padStart(2, "0")}/${d.getFullYear()}`;
}
const safeParse = (v, fb = null) => {
  try {
    return typeof v === "string" ? JSON.parse(v) : v ?? fb;
  } catch {
    return fb;
  }
};
const pxNum = (n, fb) => (Number.isFinite(Number(n)) ? Number(n) : fb);


/* === Helpers robustes pour l'hydratation du client === */
const coalesce = (...vals) =>
  vals.find((v) => v !== undefined && v !== null && String(v).trim() !== "");

function pickClientFromDevis(devis) {
  const id = coalesce(devis.client_id, devis.client?.id);
  const nom = coalesce(devis.client_nom, devis.client?.nom, devis.client?.name);

  const ligneAdresse = coalesce(
    devis.client_adresse,
    devis.adresse,
    devis.client?.adresse,
    devis.numero_voie,
    devis.client_numero_voie,
    devis.client?.numero_voie
  );
  const codePostal = coalesce(
    devis.code_postal,
    devis.client_code_postal,
    devis.client?.code_postal
  );
  const ville = coalesce(devis.ville, devis.client_ville, devis.client?.ville);

  return {
    id: id || null,
    nom: nom || "",
    numero_voie: ligneAdresse || "",
    code_postal: codePostal || "",
    ville: ville || "",
    telephone:
      coalesce(devis.telephone, devis.client_telephone, devis.client?.telephone) ||
      "",
    email: coalesce(devis.email, devis.client_email, devis.client?.email) || "",
  };
}

/* ================== Component ================== */
export default function CreerDevis() {
  /* ====== Contexte & navigation ====== */
  const { infosSociete, loading } = useContext(SocieteContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditParam = searchParams.get("edit") === "true";
  const devisParamId = searchParams.get("id");

  /* ====== Référentiels ====== */
  const [clients, setClients] = useState([]);
  useEffect(() => {
    axiosInstance.get("/clients").then((res) => setClients(res.data || []));
  }, []);

  /* ====== Layout A4 ====== */
  const pdfRef = useRef(null);

  const [lastPageUsed, setLastPageUsed] = useState(0);
  const afterRef = useRef(null);
  const [afterH, setAfterH] = useState(0);
  const footerRef = useRef(null);
  const [footerH, setFooterH] = useState(120);
  // ====== IA : génération automatique des lignes ======
  const [iaOpen, setIaOpen] = useState(false);
  const [iaDescription, setIaDescription] = useState("");
  const [iaLoading, setIaLoading] = useState(false);
// ====== IA : Chat assistant (Q/R + actions) ======
const [iaChatOpen, setIaChatOpen] = useState(false);
const [iaChatInput, setIaChatInput] = useState("");
const [iaChatLoading, setIaChatLoading] = useState(false);
// ====== IA : Vocal (Speech-to-Text) ======
const SpeechRecognition =
  typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition)
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;

const [iaMicSupported] = useState(Boolean(SpeechRecognition));
const [iaMicListening, setIaMicListening] = useState(false);
const recognitionRef = useRef(null);

const lastFinalRef = useRef("");

const [iaMessages, setIaMessages] = useState([
  { role: "assistant", content: "Salut 👋 Décris le chantier ou dis-moi quoi améliorer (phases, matériel, quantités, prix…)."}
]);

// dernière proposition reçue (actions + preview)
const [iaProposal, setIaProposal] = useState(null);

  // en haut du fichier
  const maxPageHeight = 520;                // zone A4 utile
  const MIN_SIG_FOOTER_GAP = 6;             // écart mini signatures/footer (mets 8px)
  const FOOTER_FUDGE = 20; // 12–32 px selon ton rendu

  // ====== Layout calc (remplace ton bloc actuel) ======
  const remaining = Math.max(0, maxPageHeight - lastPageUsed - afterH - footerH);
  // pousse le footer vers le bas en gardant un petit écart constant
  const spacerH = Math.max(0, remaining - MIN_SIG_FOOTER_GAP);
  const needNewPage = remaining <= 0;

  // Thème tableau (lis + réagit aux changements)
  const [tableTheme, setTableTheme] = useState(loadTableTheme());

  useEffect(() => {
    // 1) recharger si on revient sur l’onglet
    const onFocus = () => setTableTheme(loadTableTheme());
    window.addEventListener("focus", onFocus);

    // 2) recharger si un autre onglet a sauvegardé
    const off = onThemeChange(setTableTheme);

    return () => {
      window.removeEventListener("focus", onFocus);
      if (typeof off === "function") off();
    };
  }, []);


  /* ====== Mentions légales / numérotation ====== */
  const [mentionsLegales, setMentionsLegales] = useState({
    validiteDevis: "",
    mentionsSupplementaires: "",
    devis_format: "DEV-{YYYY}-{SEQ4}",
    devis_next_seq: 1,
    tva_default_rate: undefined,
    forme_juridique: undefined,
    mentions_font_px: 12,
  });
  const [mentionsSafeHtml, setMentionsSafeHtml] = useState("");

  // TVA par défaut
  const tvaParDefaut = useMemo(() => {
    const explicite = Number(mentionsLegales?.tva_default_rate);
    if (Number.isFinite(explicite)) return Math.max(0, Math.min(20, explicite));

    const haystack = [
      infosSociete?.statut,
      infosSociete?.forme,
      infosSociete?.forme_juridique,
      infosSociete?.regime,
      mentionsLegales?.forme_juridique,
      mentionsLegales?.mentionsSupplementaires,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const isAE =
      /auto\s*-?\s*entrepreneur|micro\s*-?\s*entreprise|micro\b/.test(haystack) ||
      /293\s*b|tva\s*non\s*applicable/.test(haystack);

    return isAE ? 0 : 20;
  }, [infosSociete, mentionsLegales]);

  /* ====== État principal devis ====== */
  const [dateDevis, setDateDevis] = useState(toLocalISO());
  const [editDate, setEditDate] = useState(false);
  const [numeroDevisAssigned, setNumeroDevisAssigned] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [lectureSeule, setLectureSeule] = useState(!isEditParam);
  const [devisId, setDevisId] = useState(null);
  const [saving, setSaving] = useState(false);
const [loadingDevis, setLoadingDevis] = useState(false);

  // lignes (initialisées après calcul tvaParDefaut)
  const defaultLigne = useMemo(
    () => ({
      designation: "",
      quantite: 1,
      unite: "u",
      prixUnitaire: 0,
      tva: Number(tvaParDefaut),
      type: "main_oeuvre",
      __tvaLocked: false,
    }),
    [tvaParDefaut]
  );
  const [lignes, setLignes] = useState([defaultLigne]);
  const [remisesLignes, setRemisesLignes] = useState([]);
  // --- Remise (même logique que CreerFacture) ---
  const [remiseEnabled, setRemiseEnabled] = useState(false);
  const [remiseVisible, setRemiseVisible] = useState(false);
  const [remiseMode, setRemiseMode] = useState("percent"); // "percent" | "ht" | "ttc"
  const [remiseValue, setRemiseValue] = useState(10);
  const remiseRef = useRef(null);
  const boutonRemiseRef = useRef(null);
  /* ====== Intro (WYSIWYG) ====== */
  const [introTexte, setIntroTexte] = useState(""); // texte ou HTML
  const [introSafeHtml, setIntroSafeHtml] = useState("");
  const [editingIntro, setEditingIntro] = useState(false);
  const wrapperRef = useRef(null);
  const quillRef = useRef(null);

  const [projetNom, setProjetNom] = useState("");
  const [editProjet, setEditProjet] = useState(false);

  const hasIntro = useMemo(() => {
    const txt = htmlToText(ensureHtml(introTexte || ""));
    return txt.length > 0;
  }, [introTexte]);

  // complément libre bloc client (persisté dans meta)
  const [clientComplement, setClientComplement] = useState("");
  const [delaiPaiementJours, setDelaiPaiementJours] = useState(0);
  // Panneau modal client
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientAnchorRect, setClientAnchorRect] = useState(null);

  /* ====== Client manuel (création rapide) ====== */
  const [saisieManuelle, setSaisieManuelle] = useState(false);
  const [clientManuel, setClientManuel] = useState({
    nom: "",
    numeroVoie: "",
    codePostal: "",
    ville: "",
    telephone: "",
    email: "",
    type: "particulier",
  });
  // ✅ AJOUTER ceci (au-dessus du return)

  // --- Garde "modifs non enregistrées" branchée sur UnsavedDialog
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const unsavedResolverRef = useRef(null);

  const askConfirm = useCallback(() => {
    return new Promise((resolve) => {
      unsavedResolverRef.current = resolve; // "save" | "leave" | "cancel"
      setUnsavedOpen(true);
    });
  }, []);

  const onUnsavedCancel = useCallback(() => {
    setUnsavedOpen(false);
    unsavedResolverRef.current?.("cancel");
    unsavedResolverRef.current = null;
  }, []);

  const onUnsavedExit = useCallback(() => {
    setUnsavedOpen(false);
    unsavedResolverRef.current?.("leave");
    unsavedResolverRef.current = null;
  }, []);

  const onUnsavedSaveAndExit = useCallback(() => {
    setUnsavedOpen(false);
    unsavedResolverRef.current?.("save");
    unsavedResolverRef.current = null;
  }, []);


  const btnSm = {
    fontSize: 12,
    padding: "4px 8px",
    borderRadius: 6,
    background: "#e5e7eb",
    border: "1px solid #d1d5db",
    color: "#111827",
    cursor: "pointer",
  };

  // === Totaux (identiques à CreerFacture) ===
  const calculTotalLigne = (l) => {
    const q = Math.max(0, Number(l?.quantite || 0));
    const pu = Math.max(0, Number(l?.prixUnitaire || 0));
    const tva = normalizeTva(l?.tva);
    return q * pu * (1 + tva / 100);
  };

  const totalHT = (lignes || []).reduce(
    (s, l) =>
      s + Math.max(0, Number(l.quantite || 0)) * Math.max(0, Number(l.prixUnitaire || 0)),
    0
  );
  const totalTVA = (lignes || []).reduce(
    (s, l) =>
      s +
      (Math.max(0, Number(l.quantite || 0)) *
        Math.max(0, Number(l.prixUnitaire || 0)) *
        normalizeTva(l.tva)) /
      100,
    0
  );
  const totalTTC = round2(totalHT + totalTVA);

  // Remise -> ratio uniforme (percent / HT / TTC)
  let remiseRatio = 0;
  if (remiseEnabled) {
    if (remiseMode === "percent") remiseRatio = clampPct(remiseValue) / 100;
    else if (remiseMode === "ht") remiseRatio = totalHT > 0 ? Math.min(Number(remiseValue || 0), totalHT) / totalHT : 0;
    else remiseRatio = totalTTC > 0 ? Math.min(Number(remiseValue || 0), totalTTC) / totalTTC : 0;
  }

  const remiseHT = round2(totalHT * remiseRatio);
  const remiseTVA = round2(totalTVA * remiseRatio);
  const remiseTTC = round2(remiseHT + remiseTVA);

  const netHT = round2(totalHT - remiseHT);
  const netTVA = round2(totalTVA - remiseTVA);
  const netTTC = round2(totalTTC - remiseTTC);

  const hasRemise =
    remiseEnabled &&
    (remiseMode === "percent" ? clampPct(remiseValue) > 0 : Number(remiseValue) > 0);

  const sousTotalHT = totalHT;
  const remiseLabel = !hasRemise
    ? ""
    : remiseMode === "percent"
      ? `Remise (${fmtPct(remiseValue)} %)`
      : remiseMode === "ht"
        ? `Remise (${toCurrency(remiseValue)} HT)`
        : `Remise (${toCurrency(remiseValue)} TTC)`;
  // === mêmes totaux que TableauStyleVertical ===
  function rowHT(l) {
    return Math.max(0, Number(l?.quantite || 0)) * Math.max(0, Number(l?.prixUnitaire || 0));
  }

  function remiseLigneHT(lIndex, lignes, remises) {
    const base = lignes[lIndex];
    if (!base || base.type === "note" || base.type === "pagebreak") return 0;
    const baseHT = rowHT(base);
    const list = (remises || []).filter(r => Number(r.rowIndex) === lIndex);
    let total = 0;
    for (const r of list) {
      if (r.type === "percent") total += baseHT * Math.max(0, Math.min(100, Number(r.value || 0))) / 100;
      else total += Math.max(0, Number(r.value || 0));
    }
    return Math.min(total, baseHT);
  }
  // ⬇️ AJOUTER ICI (dans CreerDevis, avant les effects)
// en haut (déjà présent normalement)
const serializeLignes = useCallback(
  () =>
    (lignes || []).map((l) => ({
      type: l?.type || "main_oeuvre",
      designation: String(l?.designation || ""),
      quantite: Math.max(0, Number(l?.quantite || 0)),
      unite: String(l?.unite || "u"),
      prixUnitaire: Math.max(0, Number(l?.prixUnitaire || 0)),
      tva: normalizeTva(l?.tva ?? 0),
    })),
  [lignes]
);


  // === Snapshot pour savoir si le formulaire est modifié ===
  const initialSnapshotRef = useRef(null);

  const currentSnapshot = useMemo(() => ({
    projetNom: (projetNom || "").trim(),
    dateDevis: dateDevis || "",
    selectedClientId: selectedClient?.id || null,
    clientComplement: (clientComplement || "").trim(),
    introTexte: ensureHtml((introTexte || "").trim()),
    remise: { enabled: !!remiseEnabled, mode: remiseMode, value: Number(remiseValue || 0) },
    lignes: serializeLignes(),
  }), [
    projetNom, dateDevis, selectedClient?.id, clientComplement,
    introTexte, remiseEnabled, remiseMode, remiseValue, lignes
  ]);

 useEffect(() => {
  if (!devisId) return;
  if (loadingDevis) return;
  initialSnapshotRef.current = JSON.stringify(currentSnapshot);
}, [devisId, loadingDevis, currentSnapshot]);


  const isDirty = useMemo(() => {
    const a = initialSnapshotRef.current || "";
    const b = JSON.stringify(currentSnapshot);
    return a !== b;
  }, [currentSnapshot]);

  /* ====== Effects ====== */
  useEffect(() => {
    setLectureSeule(!isEditParam);
  }, [isEditParam]);

  // focus auto quand l’éditeur s’ouvre
  useEffect(() => {
    if (!editingIntro) return;
    const q = quillRef.current?.getEditor?.();
    const t = setTimeout(() => q?.focus?.(), 0);
    return () => clearTimeout(t);
  }, [editingIntro]);

  // clic à l’extérieur => fermer l’éditeur
  useEffect(() => {
    if (!editingIntro) return;
    const onDown = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setEditingIntro(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [editingIntro]);
  // clic hors panneau -> fermer
  useEffect(() => {
    if (!remiseVisible) return;
    const onDocClick = (e) => {
      const inPanel = remiseRef.current?.contains(e.target);
      const inButton = boutonRemiseRef.current?.contains(e.target);
      if (!inPanel && !inButton) setRemiseVisible(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [remiseVisible]);

  // charger mentions légales
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get("/mentionslegales");
        const valSupp = data?.mentions_supplementaires ?? "";
        setMentionsLegales({
          validiteDevis: data?.validite_devis ?? "",
          mentionsSupplementaires: valSupp,
          devis_format: data?.devis_format || "DEV-{YYYY}-{SEQ4}",
          devis_next_seq: Number(data?.devis_next_seq ?? 1),
          tva_default_rate: Number.isFinite(Number(data?.tva_default_rate))
            ? Number(data?.tva_default_rate)
            : undefined,
          forme_juridique: data?.forme_juridique,
          mentions_font_px: Number(data?.mentions_font_px ?? 12), // 👈
        });


        const clean = DOMPurify.sanitize(ensureHtml(valSupp));
        const hydrated = await hydrateProtectedHtml(clean);
        setMentionsSafeHtml(hydrated);
      } catch {
        /* noop */
      }
    })();
  }, [devisParamId]);

  // charger devis si édition (+ remise + client + complément)
  useEffect(() => {
    if (!devisParamId) {
      setLignes([{ ...defaultLigne, tva: Number(tvaParDefaut), __tvaLocked: false }]);
      setDateDevis(new Date().toISOString().slice(0, 10));
      setNumeroDevisAssigned(null);
      setDevisId(null);
      setIntroTexte("");
      setProjetNom("");
      setSelectedClient(null);
      setLectureSeule(false);
      setEditProjet(true);
      setRemiseEnabled(false);
      setRemiseVisible(false);
      setRemiseMode("percent");
      setRemiseValue(10);
      setClientComplement("");
      setLoadingDevis(false);
      return;
    }
     (async () => { 
       setLoadingDevis(true);
      try {
        const res = await axiosInstance.get(`/devis/${devisParamId}`);
        const devis = res.data;

        // lignes
        let lignesOK = [];
        if (typeof devis.contenu === "string") {
          try {
            lignesOK = JSON.parse(devis.contenu);
          } catch {
            lignesOK = [];
          }
        } else if (Array.isArray(devis.contenu)) {
          lignesOK = devis.contenu;
        }

        const lignesFinales = (lignesOK || []).map((ligne) => {
          if (ligne.type === "note")
            return { type: "note", designation: ligne.designation || "" };
          if (ligne.type === "pagebreak") return { type: "pagebreak" };
          return {
            designation: ligne.designation || "",
            quantite: Math.max(0, Number(ligne.quantite || 1)),
            unite: ligne.unite || "u",
            prixUnitaire: Math.max(0, Number(ligne.prixUnitaire || 0)),
            tva: normalizeTva(ligne.tva ?? tvaParDefaut),
            type: ligne.type || "main_oeuvre",
            __tvaLocked: false,
          };
        });
        setLignes(
          lignesFinales.length === 0 ? [{ ...defaultLigne, tva: tvaParDefaut }] : lignesFinales
        );

        setDevisId(devis.id);
        setDateDevis((devis.date_devis || devis.date || "").slice(0, 10));
        setNumeroDevisAssigned(devis.numero_devis || devis.numero || null);

        // client robuste
        const baseClient = pickClientFromDevis(devis);
        setSelectedClient(baseClient);

        // fetch du client complet si adresse manquante
        if (
          baseClient.id &&
          !(baseClient.numero_voie || baseClient.ville || baseClient.code_postal)
        ) {
          try {
            const { data: fullClient } = await axiosInstance.get(`/clients/${baseClient.id}`);
            setSelectedClient({
              id: fullClient.id,
              nom: coalesce(fullClient.nom, baseClient.nom),
              numero_voie: coalesce(fullClient.numero_voie, fullClient.adresse, baseClient.numero_voie),
              code_postal: coalesce(fullClient.code_postal, baseClient.code_postal),
              ville: coalesce(fullClient.ville, baseClient.ville),
              telephone: coalesce(fullClient.telephone, baseClient.telephone),
              email: coalesce(fullClient.email, baseClient.email),
            });
          } catch { /* best effort */ }
        }

        setIntroTexte(devis.intro_texte || "");
        setLectureSeule(!isEditParam);
        setProjetNom(devis.projet_nom || "");
        setEditProjet(false);

        // meta (remise + complément client)
        // meta (remise + complément client) — CORRIGE "facture.meta" -> "devis.meta"
        let meta = devis.meta;
        if (typeof meta === "string") {
          try { meta = JSON.parse(meta); } catch { meta = null; }
        }
        const r = meta?.remise;
        if (r && (r.value ?? 0) > 0) {
          setRemiseEnabled(true);
          setRemiseMode(r.mode === "ht" || r.mode === "ttc" ? r.mode : "percent");
          setRemiseValue(Number(r.value) || 0);
          setRemiseVisible(true);
        } else {
          setRemiseEnabled(false);
          setRemiseVisible(false);
        }
        setClientComplement((meta?.client_complement || "").trim());

        const delai = Number(meta?.delai_paiement_jours);
        if (Number.isFinite(delai) && delai >= 0) setDelaiPaiementJours(delai);
     } catch (error) {
      console.error("Erreur chargement devis :", error);
      notify.error({ title: "Erreur chargement devis", message: "Impossible de charger ce devis." });
    } finally {
      setLoadingDevis(false);
      }

    })();
  }, [devisParamId, isEditParam, tvaParDefaut, defaultLigne]);

  // aligner TVA des lignes non verrouillées sur la valeur par défaut
  useEffect(() => {
    setLignes((prev) =>
      (prev || []).map((l) => (l?.__tvaLocked ? l : { ...l, tva: Number(tvaParDefaut) }))
    );
  }, [tvaParDefaut]);

  // sanitise & hydrate l’intro pour l’affichage
  useEffect(() => {
    (async () => {
      const clean = DOMPurify.sanitize(ensureHtml((introTexte || "").trim()));
      const hydrated = await hydrateProtectedHtml(clean);
      setIntroSafeHtml(hydrated);
    })();
  }, [introTexte]);

  // mesures layout
  useLayoutEffect(() => {
    setAfterH(afterRef.current?.offsetHeight ?? 0);
  }, [totalHT, totalTVA, totalTTC, lignes.length]);

  useLayoutEffect(() => {
  const h = footerRef.current?.offsetHeight ?? 120;
  setFooterH(h);
}, [lignes.length, remisesLignes.length, mentionsSafeHtml, lastPageUsed]);


  /* ====== Actions ====== */
  const supprimerLigne = (index) => setLignes((prev) => prev.filter((_, i) => i !== index));

  const handleChange = (index, field, value) => {
    setLignes((prev) =>
      prev.map((ligne, i) => {
        if (i !== index) return ligne;
        if (field === "quantite" || field === "prixUnitaire") {
          const v = Math.max(0, Number(value || 0));
          return { ...ligne, [field]: v };
        }
        if (field === "tva") {
          const v = normalizeTva(value);
          return { ...ligne, tva: v, __tvaLocked: true };
        }
        return { ...ligne, [field]: value };
      })
    );
  };

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRow, setPickerRow] = useState(null);
  const openPickerFor = (i) => {
    setPickerRow(i);
    setPickerOpen(true);
  };

  const societeId = infosSociete?.id ?? infosSociete?.societe_id ?? null;
  const projectName = (infosSociete?.slug || infosSociete?.nom || "").toString().trim();

  // transforme une ligne en payload "désignation" (compat champs A/B)
  function toDesignationPayload(it, scope = {}) {
    const raw = String(it.label || "").trim();
    const pu = Math.max(0, Number(it.prixUnitaire || it.prix || 0));
    const tva = (it.tva === 0 || Number.isFinite(Number(it.tva)))
      ? Number(it.tva)
      : Number(tvaParDefaut); // ✅ pas de 20 forcé

    // ✅ n’enveloppe pas en <p>…</p> si ce n’est pas déjà du HTML
    const labelOrText = raw;

    return {
      label: labelOrText,     // back A
      prixUnitaire: pu,
      tva,
      titre: labelOrText,     // back B (compat)
      prix: pu,
      ...scope,               // ✅ scope ici
    };
  }


  // dédupe par libellé “normalisé” (on strip les balises pour éviter <p>..</p> vs texte)
  const keyOf = (s = "") => String(s)
    .replace(/<[^>]+>/g, " ")        // strip tags
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().trim();

  function buildDesignationItems(lignes, tvaParDefaut) {
    const map = new Map();
    for (const l of (lignes || [])) {
      if (!l || l.type === "note" || l.type === "pagebreak") continue;
      const label = String(l.designation || "").trim();
      if (!label) continue;
      const k = keyOf(label);
      if (!k) continue;
      map.set(k, {
        label, // conserve HTML si présent
        prixUnitaire: Math.max(0, Number(l.prixUnitaire || 0)),
        tva: (l.tva === 0 || Number.isFinite(Number(l.tva))) ? Number(l.tva) : Number(tvaParDefaut),
      });
    }
    return [...map.values()];
  }


  async function saveCatalogIfAny() {
    const items = buildDesignationItems(lignes, tvaParDefaut);
    if (!items.length) return;

    const scope = {};
    if (societeId != null) scope.societe_id = societeId;
    if (projectName) scope.project = projectName;

    for (const it of items) {
      const payload = {
        // compat A/B
        label: it.label,
        prixUnitaire: Number(it.prixUnitaire || 0),
        tva: (it.tva === 0 || Number.isFinite(Number(it.tva))) ? Number(it.tva) : Number(tvaParDefaut),
        titre: it.label,
        prix: Number(it.prixUnitaire || 0),
        // scope EXACTEMENT comme la page /designations
        ...scope,
      };
      try {
        await axiosInstance.post("/designations", payload);
      } catch (err) {
        // 409 = déjà présent -> on ignore
        if (err?.response?.status !== 409) {
          console.warn("create designation:", err?.response?.data || err);
        }
      }
    }
  }
 // ====== IA : génération des lignes de devis ======
// ====== IA : assistant devis (actions sur tableau) ======
async function handleIaGenerate() {
  const desc = iaDescription.trim();
  if (!desc) {
    notify.warning({
      title: "Description vide",
      message: "Décrivez le chantier pour que l’IA propose des lignes.",
    });
    return;
  }

  try {
    setIaLoading(true);

    const payload = {
      message: desc,
      metier:
        infosSociete?.activite ||
        infosSociete?.secteur ||
        infosSociete?.categorie ||
        "",
      lignes_actuelles: serializeLignes(), // ✅ IA voit le tableau
      catalogue_articles: null,            // ✅ optionnel: tu peux mettre ton catalogue plus tard
    };

    const { data } = await axiosInstance.post(
      "/ia/devis-assistant",
      payload,
      { timeout: 60000 } // ✅ timeout FRONT uniquement
    );

    const actions = Array.isArray(data?.actions) ? data.actions : [];
    if (!actions.length) {
      notify.error({
        title: "IA",
        message: "Aucune action proposée. Donnez plus de détails et réessayez.",
      });
      return;
    }

  applyIaActionsToLignes(actions);


    // messages utiles
    if (data?.assistant_message) {
      notify.success({ title: "IA", message: data.assistant_message });
    }
    if (Array.isArray(data?.questions) && data.questions.length) {
      notify.warning({ title: "IA – questions", message: data.questions.join(" • ") });
    }

    setIaOpen(false);
    setIaDescription("");
  } catch (e) {
    if (e?.code === "ECONNABORTED") {
      notify.error({ title: "Échec IA", message: "IA trop lente (timeout). Réessaie." });
      return;
    }
    console.error("IA devis-assistant:", e);
    notify.error({ title: "Échec IA", message: extractErr(e) });
  } finally {
    setIaLoading(false);
  }
}
// ===================== IA CHAT HELPERS =====================

// Normalise une row IA et force TVA gérée par le SaaS
function normalizeIaRow(row) {
  if (!row || typeof row !== "object") return null;

  if (row.type === "note") return { type: "note", designation: String(row.designation || "") };
  if (row.type === "pagebreak") return { type: "pagebreak" };

  const type = ["main_oeuvre", "materiaux", "forfait"].includes(row.type) ? row.type : "main_oeuvre";
  const designation = String(row.designation || "").trim();
  if (!designation) return null;

  const quantite = Math.max(0.01, Number(row.quantite ?? 1));
  const unite = String(row.unite || "u").trim().slice(0, 10);
  const prixUnitaire = Math.max(0, Number(row.prixUnitaire ?? row.prixUnitaireHT ?? 0));

  // ✅ TVA SaaS : si null/undefined -> tvaParDefaut ; et on laisse __tvaLocked=false
  const tva = (row.tva === null || row.tva === undefined) ? Number(tvaParDefaut) : normalizeTva(row.tva);

  return { type, designation, quantite, unite, prixUnitaire, tva, __tvaLocked: false };
}

// Applique les actions IA au tableau (en normalisant TVA)
function applyIaActionsToLignes(actions = []) {
  setLignes((prev) => {
    const arr = Array.isArray(prev) ? [...prev] : [];

    for (const a of actions) {
      if (!a || !a.op) continue;

      if ((a.op === "append" || a.op === "insert") && a.row) {
        const row = normalizeIaRow(a.row);
        if (!row) continue;

        if (a.op === "append") arr.push(row);
        else {
          const idx = Math.max(0, Math.min(arr.length, Number(a.index ?? arr.length)));
          arr.splice(idx, 0, row);
        }
      }

      if (a.op === "update") {
        const idx = Number(a.index);
        if (!Number.isFinite(idx) || !arr[idx]) continue;

        const patch = { ...(a.patch || {}) };

        // ✅ TVA SaaS : si patch.tva null => tvaParDefaut
        if ("tva" in patch && (patch.tva === null || patch.tva === undefined)) {
          patch.tva = Number(tvaParDefaut);
        }
        if ("tva" in patch) patch.tva = normalizeTva(patch.tva);

        arr[idx] = { ...arr[idx], ...patch };
      }

      if (a.op === "delete") {
        const idx = Number(a.index);
        if (Number.isFinite(idx) && idx >= 0 && idx < arr.length) arr.splice(idx, 1);
      }

      if (a.op === "move") {
        const from = Number(a.from), to = Number(a.to);
        if (!Number.isFinite(from) || !Number.isFinite(to)) continue;
        if (from < 0 || from >= arr.length) continue;
        const [it] = arr.splice(from, 1);
        const dest = Math.max(0, Math.min(arr.length, to));
        arr.splice(dest, 0, it);
      }
    }

    return arr;
  });

  notify.success({ title: "IA", message: "Proposition appliquée au devis." });
}

// Envoi d'un message au chat IA
async function iaChatSend() {
  const msg = iaChatInput.trim();
  if (!msg || iaChatLoading) return;

  setIaMessages((prev) => [...prev, { role: "user", content: msg }]);
  setIaChatInput("");
  setIaChatLoading(true);

  try {
    const payload = {
      message: msg,
      metier: infosSociete?.activite || infosSociete?.secteur || infosSociete?.categorie || "",
      lignes_actuelles: serializeLignes(),
      catalogue_articles: null,
    };

    const { data } = await axiosInstance.post("/ia/devis-assistant", payload, { timeout: 60000 });

    if (data?.assistant_message) {
      setIaMessages((prev) => [...prev, { role: "assistant", content: data.assistant_message }]);
    }

    if (Array.isArray(data?.questions) && data.questions.length) {
      setIaMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Questions :\n- " + data.questions.join("\n- ") },
      ]);
    }

    const actions = Array.isArray(data?.actions) ? data.actions : [];
    setIaProposal({
      assistant_message: data?.assistant_message || "",
      questions: Array.isArray(data?.questions) ? data.questions : [],
      actions,
      preview: Array.isArray(data?.preview) ? data.preview : null,
    });

    if (!actions.length) {
      notify.warning({ title: "IA", message: "Aucune action proposée. Donne plus de détails." });
    }
  } catch (e) {
    if (e?.code === "ECONNABORTED") {
      notify.error({ title: "Échec IA", message: "IA trop lente (timeout). Réessaie." });
    } else {
      notify.error({ title: "Échec IA", message: extractErr(e) });
    }
  } finally {
    setIaChatLoading(false);
  }
}
function iaMicToggle() {
  if (!SpeechRecognition) {
    notify.error({ title: "Micro", message: "Reconnaissance vocale non supportée sur ce navigateur." });
    return;
  }

  // Stop si déjà en écoute
  if (iaMicListening) {
    try { recognitionRef.current?.stop?.(); } catch {}
    setIaMicListening(false);
    return;
  }

  // Init une fois
  if (!recognitionRef.current) {
    const rec = new SpeechRecognition();
    rec.lang = "fr-FR";
    rec.continuous = false;      // stop auto après une phrase
    rec.interimResults = false;  // pas de texte "en direct"

    rec.maxAlternatives = 1;

   rec.onstart = () => {
  lastFinalRef.current = "";  // reset anti-doublon à chaque nouvelle écoute
  setIaMicListening(true);
};


    rec.onerror = (e) => {
      console.warn("Speech error:", e);
      setIaMicListening(false);
      // notifs utiles
      if (e?.error === "not-allowed" || e?.error === "service-not-allowed") {
        notify.error({ title: "Micro refusé", message: "Autorise le micro dans le navigateur." });
      }
    };

    rec.onend = () => setIaMicListening(false);

    rec.onresult = (event) => {
  // comme interimResults=false, on prend uniquement du FINAL
  const res = event.results?.[event.resultIndex];
  const txt = (res?.[0]?.transcript || "").trim();
  if (!txt) return;

  // anti-doublon (Chrome renvoie parfois 2 fois la même phrase finale)
  if (txt === lastFinalRef.current) return;
  lastFinalRef.current = txt;

  setIaChatInput((prev) => {
    const base = (prev || "").trim();
    return base ? `${base} ${txt}` : txt;
  });
};

    recognitionRef.current = rec;
  }

  // Start
  try {
    recognitionRef.current.start();
  } catch (e) {
    // start peut throw si déjà started
    console.warn("Speech start:", e);
  }
}

  // ================== SAUVEGARDE DEVIS ==================
  async function saveDevis({ redirectAfterSave = false } = {}) {
    if (saving) return null;

    // validations rapides
    if (!projetNom?.trim()) {
      notify.warning({ title: "Projet manquant", message: "Renseignez un nom de projet." });
      return null;
    }
    if (!selectedClient?.id) {
      notify.warning({ title: "Client requis", message: "Sélectionnez un client." });
      return null;
    }

    // lignes propres : on enlève les lignes vides & on normalise
  // lignes normalisées
const lignesArray = serializeLignes();

// blocage si tout est vraiment vide (aucune désignation, quantité, prix, note ou pagebreak)
if (
  !lignesArray.some(
    (l) =>
      l.type === "pagebreak" ||
      l.type === "note" ||
      l.designation ||
      l.quantite > 0 ||
      l.prixUnitaire > 0
  )
) {
  notify.warning({
    title: "Lignes vides",
    message: "Ajoutez au moins une ligne.",
  });
  return null;
}


    // meta enrichi
    const meta = {
      client_complement: (clientComplement || "").trim(),
      delai_paiement_jours: Number.isFinite(Number(delaiPaiementJours))
        ? Number(delaiPaiementJours)
        : 0,
      remise: remiseEnabled
        ? {
          mode: remiseMode,                      // 'percent' | 'ht' | 'ttc'
          value: Number(remiseValue || 0),
        }
        : { mode: "percent", value: 0 },
    };

    const payload = {
      tenant_id: readTenantId(),
      client_id: Number(selectedClient.id),
      projet_nom: projetNom.trim(),
      date_devis: (dateDevis || new Date().toISOString().slice(0, 10)),
      contenu: lignesArray,
      intro_texte: ensureHtml(introTexte || ""),
      total_ttc: netTTC,
      validite: (mentionsLegales?.validiteDevis || "").trim() || null,
      meta,
    };

    setSaving(true);
    try {
      let savedId = devisId ?? null;

      if (devisId) {
        // 🔄 mise à jour
        const { data } = await axiosInstance.put(`/devis/${devisId}`, payload, {
          headers: { "Content-Type": "application/json" },
        });
        savedId = data?.id ?? devisId;
        notify.success({ title: "Devis mis à jour", message: "Modifications enregistrées." });
      } else {
        // 🆕 création
        const { data } = await axiosInstance.post(`/devis`, payload, {
          headers: { "Content-Type": "application/json" },
        });
        savedId = data?.id ?? null;

        setDevisId(savedId);
        setNumeroDevisAssigned(data?.numero_devis ?? null);
        setLectureSeule(true);
        saveCatalogIfAny().catch(() => { });
        notify.success({ title: "Devis créé", message: "Succès." });
      }

      // on considère qu’il n’y a plus de modifs non enregistrées
      initialSnapshotRef.current = JSON.stringify(currentSnapshot);

      // 👉 redirection automatique vers AfficherDevis si demandé
      if (redirectAfterSave && savedId) {
        // ADAPTE l’URL si ton routeur a un autre chemin
        navigate(`/afficher-devis?id=${savedId}`);
      }

      return savedId;
    } catch (e) {
      console.error("SAVE DEVIS:", e);
      notify.error({ title: "Échec", message: extractErr(e) });
      throw e;
    } finally {
      setSaving(false);
    }
  }

  // Bouton "Enregistrer" : on sauvegarde + on va sur AfficherDevis
  async function handleSaveDevis() {
    await saveDevis({ redirectAfterSave: true });
  }


  const { safeNavigate, confirmLeave } = useUnsavedChanges({
    isDirty,
    // ⚠️ on sauvegarde SANS redirection automatique
    // pour laisser useUnsavedChanges aller vers la destination voulue
    onSave: () => saveDevis({ redirectAfterSave: false }),
    navigate,
    askConfirm, // UnsavedDialog ("Enregistrer et quitter", etc.)
  });
  // Expose le garde globalement pour le sidebar / autres composants
  useEffect(() => {
    window.__FLOWDEVIS_CONFIRM_LEAVE__ = confirmLeave;
    return () => {
      // On nettoie seulement si c'est toujours la même fonction
      if (window.__FLOWDEVIS_CONFIRM_LEAVE__ === confirmLeave) {
        delete window.__FLOWDEVIS_CONFIRM_LEAVE__;
      }
    };
  }, [confirmLeave]);

  /* ====== Export PDF (aperçu) ====== */
  function exportPDF() {
    const el = document.querySelector(".a4-page");
    if (!el) return;
    const opt = {
      margin: 0,
      filename: "devis.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: {
        mode: ["css", "avoid-all"],
        before: ".html2pdf__page-break",
        avoid: [".no-split", ".table-pdf", ".sig-block", ".totaux-block", ".footer-apercu"],
      },
    };
    document.body.classList.add("exporting");
    html2pdf().set(opt).from(el).save().finally(() => document.body.classList.remove("exporting"));
  }
  function MentionsBlock({ html, fontPx = 12 }) {
    if (!html) return null;
    return (
      <div
        className="mentions-legales"
        style={{ fontSize: `${fontPx}px`, lineHeight: 1.5 }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  /* ====== Rendu Société (texte + logos sécurisés) ====== */
  const blocHtmlRaw = useMemo(
    () => String(infosSociete?.bloc_html || "").trim(),
    [infosSociete?.bloc_html]
  );

  const [blocHtmlSafe, setBlocHtmlSafe] = useState("");

  useEffect(() => {
    (async () => {
      const clean = DOMPurify.sanitize(blocHtmlRaw);
      const hydrated = await hydrateProtectedHtml(clean);
      setBlocHtmlSafe(hydrated);
    })();
  }, [blocHtmlRaw]);

  const posTexteRaw = useMemo(
    () => safeParse(infosSociete?.positiontexte, infosSociete?.positiontexte),
    [infosSociete?.positiontexte]
  );

  const posTexte = useMemo(
    () => ({
      x: pxNum(posTexteRaw?.x, 40),
      y: pxNum(posTexteRaw?.y, 120),
      width: pxNum(posTexteRaw?.width, 320),
      height: pxNum(posTexteRaw?.height, null),
    }),
    [posTexteRaw]
  );

  if (loading || !infosSociete) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-500" />
      </div>
    );
  }

 
// juste au même endroit
const logosOnPage = useMemo(() => {
  // 1) Layout complet enregistré (nouveau système)
  const arr = safeParse(infosSociete.logos_on_page, infosSociete.logos_on_page);
  if (Array.isArray(arr) && arr.length) return arr;

  // 2) Fallback ancien système : position + logo principal
  const p = safeParse(infosSociete.positionlogo, infosSociete.positionlogo);

  // on prend d’abord les nouveaux champs, puis l’ancien
  const principalFileRef =
    infosSociete.logo_principal_file_id ??
    infosSociete.logo_file_id ??
    infosSociete.logoPrincipalFileId ??
    infosSociete.logo; // compat ancien schéma

  if (principalFileRef && p) {
    return [
      {
        id: "pl-fallback",
        libId: "lib-0",
        fileId: principalFileRef,  // ⬅ important
        src: principalFileRef,     // ⬅ compat si tu as encore du code qui lit src
        x: pxNum(p?.x, 420),
        y: pxNum(p?.y, 40),
        width: pxNum(p?.width, 140),
        height: pxNum(p?.height, 70),
      },
    ];
  }
  return [];
}, [infosSociete]);

  const getTexteStyle = () => ({
    position: "absolute",
    transform: `translate(${posTexte.x}px, ${posTexte.y}px)`,
    width: posTexte.width ? `${posTexte.width}px` : undefined,
    height: posTexte.height ? `${posTexte.height}px` : "auto",
    zIndex: 45,
  });

  return (
    <div className="p-1" style={themeStyleVars(tableTheme)}>
      {/* ===== Barre titre + barre d’actions ===== */}
      <div className="no-print mb-6">
        {/* Titre plein large */}
        <h1 className="text-2xl font-semibold text-slate-800 mb-6">
          {devisId
            ? `Modification du Devis n° ${formatNumeroDevis(numeroDevisAssigned)}`
            : "Création d’un nouveau Devis"}
        </h1>

        {/* Barre blanche sous le titre */}
        <div className="w-full bg-white border border-slate-200 rounded-lg shadow-sm px-3 py-2
                grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-2">

          {/* Colonne gauche vide (sert d'équilibrage en desktop) */}
          <div className="hidden sm:block" />

          {/* PROJET — centré */}
          <div className="flex items-center justify-center gap-3 min-w-0">
            <span className="text-[14px] font-medium uppercase tracking-wide text-slate-600 shrink-0">
              Projet
            </span>

            {(!devisId || editProjet) && !lectureSeule ? (
              <>
                <input
                  className="h-9 px-4 rounded-full border border-slate-300 bg-white text-sm
                     w-[min(560px,90vw)] focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-center"
                  placeholder="Ex : Rénovation SDB – Mme Dupont"
                  value={projetNom}
                  onChange={(e) => setProjetNom(e.target.value)}
                />
                {devisId && (
                  <>
                    <button
                      type="button"
                      className="text-xs text-slate-600 hover:underline px-1"
                      onClick={() => setEditProjet(false)}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="text-xs px-3 py-1.5 rounded-full bg-sky-700 text-white hover:bg-sky-600"
                      onClick={async () => {
                        try {
                          await axiosInstance.put(`/devis/${devisId}`, { projet_nom: projetNom.trim() });
                          setEditProjet(false);
                        } catch (e) {
                          alert(e?.response?.data?.message || "❌ Erreur enregistrement du projet");
                        }
                      }}
                    >
                      Enregistrer
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="px-4 py-1.5 h-9 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-800
                        truncate text-center min-w-[240px] w-[min(560px,90vw)]">
                  {projetNom || <em className="text-slate-400">— non défini —</em>}
                </div>
                {devisId && !lectureSeule && (
                  <button
                    type="button"
                    className="text-xs text-sky-700 hover:underline"
                    onClick={() => setEditProjet(true)}
                  >
                    Modifier
                  </button>
                )}
              </>
            )}
          </div>

          {/* Actions à droite */}
          <div className="flex justify-end items-center gap-2">
            <SkyTab
              variant="primary"
              onClick={handleSaveDevis}
              disabled={saving}
              className="font-medium"
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </SkyTab>
          </div>
        </div>

      </div>

      {iaOpen && (
        <div className="no-print fixed inset-0 z-[1400] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-xl p-4">
            <h3 className="text-sm font-semibold mb-2">
              Décrire le chantier pour générer une proposition de devis
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Exemple : “Remplacement d’un ballon d’eau chaude 200&nbsp;L dans
              un appartement au 3e étage, évacuation de l’ancien matériel,
              petites fournitures, mise en service”.
            </p>
            <textarea
              className="w-full border rounded-md px-2 py-1 text-sm min-h-[120px]"
              value={iaDescription}
              onChange={(e) => setIaDescription(e.target.value)}
              placeholder="Décrivez le chantier, les pièces, les matériels, les contraintes, l’accès, etc."
            />
            <div className="mt-3 flex justify-between items-center gap-2">
              <button
                type="button"
                className="px-2 py-1 text-xs rounded border"
                onClick={() => {
                  if (iaLoading) return;
                  setIaOpen(false);
                  setIaDescription("");
                }}
                disabled={iaLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded-full bg-sky-700 text-white hover:bg-sky-600 disabled:opacity-60"
                onClick={handleIaGenerate}
                disabled={iaLoading}
              >
                {iaLoading ? "Génération en cours…" : "Générer les lignes avec l’IA"}
              </button>
            </div>
          </div>
        </div>
      )}
{iaChatOpen && (
  <div className="no-print fixed inset-0 z-[1400] flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">Assistant IA (chat)</h3>
        <button
          type="button"
          className="text-xs px-2 py-1 border rounded"
          onClick={() => {
  if (iaChatLoading) return;
  try { recognitionRef.current?.stop?.(); } catch {}
  setIaMicListening(false);
  setIaChatOpen(false);
}}

        >
          Fermer
        </button>
      </div>

      <div className="border rounded p-2 h-[320px] overflow-y-auto bg-slate-50 text-sm space-y-2">
        {iaMessages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
            <div
              className={`inline-block max-w-[90%] px-3 py-2 rounded-lg ${
                m.role === "user" ? "bg-sky-700 text-white" : "bg-white border"
              }`}
              style={{ whiteSpace: "pre-wrap" }}
            >
              {m.content}
            </div>
          </div>
        ))}
      </div>

<div className="mt-2 flex gap-2 items-center">
  <button
    type="button"
    className="px-3 py-2 rounded border text-sm disabled:opacity-60"
    onClick={iaMicToggle}
    disabled={!iaMicSupported}
    title={!iaMicSupported ? "Non supporté sur ce navigateur" : (iaMicListening ? "Arrêter" : "Parler")}
  >
    {iaMicListening ? "🎙️ Stop" : "🎙️ Micro"}
  </button>

  <input
    className="flex-1 border rounded px-2 py-2 text-sm"
    value={iaChatInput}
    onChange={(e) => setIaChatInput(e.target.value)}
    placeholder="Parle ou écris ici…"
    onKeyDown={(e) => e.key === "Enter" && iaChatSend()}
  />

  <button
    type="button"
    className="px-3 py-2 rounded bg-sky-700 text-white text-sm disabled:opacity-60"
    onClick={iaChatSend}
    disabled={iaChatLoading}
  >
    {iaChatLoading ? "…" : "Envoyer"}
  </button>
</div>


      {iaProposal?.actions?.length > 0 && (
        <div className="mt-3 flex items-center justify-between">
          <div className="text-xs text-slate-600">
            Proposition prête ({iaProposal.actions.length} actions)
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="px-3 py-1.5 rounded border text-xs"
              onClick={() => setIaProposal(null)}
            >
              Ignorer
            </button>
            <button
              type="button"
              className="px-3 py-1.5 rounded bg-emerald-700 text-white text-xs"
              onClick={() => applyIaActionsToLignes(iaProposal.actions)}
            >
              Appliquer au devis
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
)}

      <div className="a4-page relative z-0 saved" ref={pdfRef}>
      

       {/* ===== Logos + Texte société ===== */}
{logosOnPage.map((pl) => {
  const rawSrc = pl.src || pl.url || "";
  const style = {
    transform: `translate(${pxNum(pl.x, 0)}px, ${pxNum(pl.y, 0)}px)`,
    width: `${pxNum(pl.width, 140)}px`,
    height: `${pxNum(pl.height, 70)}px`,
    zIndex: 80,
    pointerEvents: "none",
  };

  const isFileObj =
    typeof rawSrc !== "string" &&
    typeof File !== "undefined" &&
    rawSrc instanceof File;

  // cas normal : URL Cloudinary déjà complète
  let src = "";
  if (!isFileObj) {
    if (/^https?:\/\//i.test(rawSrc)) {
      // ex: https://res.cloudinary.com/...
      src = rawSrc;
    } else {
      // ancien format: "55" ou "/api/files/55"
      const idMatch = String(rawSrc).match(/(\d+)$/);
      const id = idMatch ? idMatch[1] : null;
      if (id) {
        const BASE = (axiosInstance?.defaults?.baseURL || "").replace(/\/+$/, "");
        const ROOT = BASE.replace(/\/api\/?$/i, "");
        src = `${ROOT}/api/files/${id}`;
      }
    }
  }

  return (
    <div key={pl.id || src || Math.random()} className="absolute" style={style}>
      {isFileObj ? (
        <img
          src={URL.createObjectURL(rawSrc)}
          alt="Logo société"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      ) : (
        src && (
          <img
            src={src}
            alt="Logo société"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        )
      )}
    </div>
  );
})}


        {blocHtmlSafe && (
          <div style={getTexteStyle()}>
            <div
              className="ql-editor !p-0 !leading-snug"
              style={{ overflow: "visible", maxHeight: "none" }}
              dangerouslySetInnerHTML={{ __html: blocHtmlSafe }}
            />
          </div>
        )}

        {/* ===== Bloc client (imprimable) ===== */}
        <ClientDisplayCard
          selectedClient={selectedClient}
          clientComplement={clientComplement}
          onOpenModal={(rect) => {
            setClientAnchorRect(rect);
            setClientModalOpen(true);
          }}
        />

        {/* ===== Panneau client (portal) ===== */}
        {clientModalOpen &&
          createPortal(
            <ClientModal
              clients={clients}
              selectedClient={selectedClient}
              setSelectedClient={setSelectedClient}
              saisieManuelle={saisieManuelle}
              setSaisieManuelle={setSaisieManuelle}
              clientManuel={clientManuel}
              setClientManuel={setClientManuel}
              clientComplement={clientComplement}
              setClientComplement={setClientComplement}
              anchorRect={clientAnchorRect}
              onClose={() => setClientModalOpen(false)}
            />,
            document.body
          )}

        {unsavedOpen && (
          <UnsavedDialog
            open={unsavedOpen}
            onCancel={onUnsavedCancel}        // -> "Annuler" (rester sur la page)
            onExit={onUnsavedExit}            // -> "Quitter sans enregistrer"
            onSaveAndExit={onUnsavedSaveAndExit} // -> "Enregistrer et quitter"
          />
        )}

        {/* ===== Numéro + Date ===== */}
        <div className="absolute top-[280px] left-[40px] w-[260px] text-sm z-[95] pointer-events-auto">
          <div className="font-bold">Devis n° {formatNumeroDevis(numeroDevisAssigned)}</div>
          <div className="mt-1 flex items-center gap-2">
            {editDate ? (
              <input
                type="date"
                value={dateDevis}
                onChange={(e) => setDateDevis(e.target.value)}
                onBlur={() => setEditDate(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur();
                }}
                autoFocus
                className="border p-1 rounded text-xs w-full"
              />
            ) : (
              <>
                <div className="text-xs text-gray-700">Émis le {formatDateFR(dateDevis)}</div>
                <button
                  type="button"
                  className="no-print text-xs text-sky-700 hover:underline"
                  onClick={() => setEditDate(true)}
                  title="Modifier la date"
                >
                  Modifier
                </button>
              </>
            )}
          </div>
        </div>

        {/* ===== CONTENU ===== */}
        <div className="a4-content mt-[300px] px-6 ">
          {/* NOTE D’INTRO – visible “Ajouter une note…”, WYSIWYG au clic */}
          <div
            ref={wrapperRef}
            className="intro-inline relative z-[200] w-full mb-2"
            style={{ pointerEvents: "auto" }}
          >
            {/* LECTURE : placeholder si vide */}
            {!editingIntro && !hasIntro && (
              <div
                className="intro-placeholder"
                onClick={() => setEditingIntro(true)}
                title="Ajouter une note"
                aria-label="Ajouter une note"
              >
                Ajouter une note…
              </div>
            )}

            {/* LECTURE : affichage du texte si présent */}
            {!editingIntro && hasIntro && (
              <div className="cursor-text" onClick={() => setEditingIntro(true)}>
                <div
                  className="ql-editor !p-0 leading-relaxed text-sm text-slate-800"
                  dangerouslySetInnerHTML={{ __html: introSafeHtml }}
                />
              </div>
            )}

            {/* ÉDITION : WYSIWYG */}
            {editingIntro && (
              <>
                <div id="toolbar-intro" className="ql-toolbar ql-snow intro-toolbar">
                  <select className="ql-size" defaultValue="">
                    <option value="">Défaut</option>
                    <option value="12px">12</option>
                    <option value="14px">14</option>
                    <option value="16px">16</option>
                    <option value="18px">18</option>
                    <option value="24px">24</option>
                    <option value="32px">32</option>
                  </select>
                  <button className="ql-bold" />
                  <button className="ql-italic" />
                  <button className="ql-underline" />
                  <select className="ql-align" />
                  <button className="ql-list" value="ordered" />
                  <button className="ql-list" value="bullet" />
                  <button className="ql-link" />
                  <button className="ql-clean" />
                </div>

                <ReactQuill
                  key="intro-edit"                         // force un remount propre
                  ref={quillRef}
                  theme="snow"
                  value={ensureHtml(introTexte)}
                  onChange={(html) => setIntroTexte(html)}
                  placeholder="Écrire votre note…"
                  modules={{ toolbar: { container: "#toolbar-intro" } }}
                  formats={[
                    "size", "bold", "italic", "underline", "align",
                    "list", "bullet", "link", "clean",
                  ]}
                  className="intro-editor"
                  onKeyDown={(e) => e.key === "Escape" && setEditingIntro(false)}
                />
              </>
            )}
          </div>

          {/* Bandeau IA : génération automatique du devis */}
          <div className="no-print mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-slate-500">
              Nouveauté : décrivez le chantier et FlowDevis IA propose les
              lignes du devis (modifiables à la main).
            </div>
            <button
              type="button"
              className="px-3 py-1.5 rounded-full border border-sky-600 text-xs font-medium text-sky-700 hover:bg-sky-50"
              onClick={() => setIaOpen(true)}
            >
              IA – Proposer le devis
            </button>
            <button
  type="button"
  className="px-3 py-1.5 rounded-full border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50"
  onClick={() => setIaChatOpen(true)}
>
  IA – Chat devis
</button>

          </div>


          {/* Tableau des lignes */}
          <TableauStyleVertical
            lignes={lignes}
            handleChange={handleChange}
            supprimerLigne={supprimerLigne}
            onPickFromCatalog={(idx) => openPickerFor(idx)}
            totalLigne={calculTotalLigne}
            lectureSeule={lectureSeule}

            onReorder={setLignes}
            pageBreakGap={48}
            onLastPageHeight={setLastPageUsed}
            tvaParDefaut={tvaParDefaut}
            societeId={societeId}
            projectName={projectName}

          />

          <DesignationPickerModal
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onSelect={(it) => {
              if (pickerRow == null) return;
              const set = (field, v) =>
                setLignes((prev) => prev.map((l, i) => (i === pickerRow ? { ...l, [field]: v } : l)));
              set("designation", it.label);
              set("prixUnitaire", Math.max(0, Number(it.prixUnitaire)));
              set("tva", normalizeTva(it.tva ?? tvaParDefaut));
              set("type", it.type ?? "materiaux");
              setPickerOpen(false);
              setPickerRow(null);
            }}
          />
          <div ref={afterRef}>
            <div className="no-print mt-2 flex justify-end">
              <button
                ref={boutonRemiseRef}
                type="button"
                className="px-3 py-1 text-xs rounded border bg-gray-100 hover:bg-gray-200 text-slate-600"
                onClick={() => {
                  if (!remiseEnabled) setRemiseEnabled(true);
                  setRemiseVisible(true);
                }}
                title={
                  remiseEnabled && remiseMode === "percent"
                    ? `Remise active : ${remiseValue}%`
                    : "Appliquer une remise"
                }
              >
                Appliquer une remise
              </button>
            </div>

            {remiseVisible && (
              <div
                ref={remiseRef}
                className="no-print mt-2 mb-1 p-2 border rounded bg-gray-50 text-xs"
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                  <div>
                    <label className="block font-medium mb-1">Type de remise</label>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        className={`px-2 py-1 rounded border ${remiseMode === "percent" ? "bg-white" : "bg-gray-100"}`}
                        onClick={() => setRemiseMode("percent")}
                      >
                        %
                      </button>
                      <button
                        type="button"
                        className={`px-2 py-1 rounded border ${remiseMode === "ht" ? "bg-white" : "bg-gray-100"}`}
                        onClick={() => setRemiseMode("ht")}
                      >
                        HT
                      </button>
                      <button
                        type="button"
                        className={`px-2 py-1 rounded border ${remiseMode === "ttc" ? "bg-white" : "bg-gray-100"}`}
                        onClick={() => setRemiseMode("ttc")}
                      >
                        TTC
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium mb-1">
                      {remiseMode === "percent" ? "Valeur (%)" : remiseMode === "ht" ? "Montant HT (€)" : "Montant TTC (€)"}
                    </label>
                    <input
                      type="number"
                      step={remiseMode === "percent" ? "0.1" : "0.01"}
                      min="0"
                      className="w-full border rounded px-2 py-1 text-right"
                      value={remiseValue}
                      onChange={(e) => setRemiseValue(e.target.value)}
                      placeholder={remiseMode === "percent" ? "ex: 10" : "ex: 150.00"}
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="flex-1 px-2 py-1 rounded border bg-white"
                      onClick={() => setRemiseVisible(false)}
                      title="Fermer l’aperçu (remise conservée)"
                    >
                      Fermer
                    </button>
                    <button
                      type="button"
                      className="flex-1 px-2 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-white"
                      onClick={() => {
                        setRemiseEnabled(false);
                        setRemiseVisible(false);
                        setRemiseValue(0);
                      }}
                      title="Retirer complètement la remise"
                    >
                      Retirer
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="mt-2 flex justify-end">
              <div className="w-[320px] text-sm">
                <div className="flex justify-between py-1 border-t">
                  <span>Sous-total HT</span>
                  <span>{toCurrency(totalHT)}</span>
                </div>
                {remiseVisible && hasRemise && (
                  <>
                    <div className="flex justify-between py-1">
                      <span>{remiseLabel}</span>
                      <span className="text-slate-900">- {toCurrency(remiseHT)}</span>
                    </div>
                    <div className="flex justify-between py-1 font-medium">
                      <span>Sous-total après remise (HT)</span>
                      <span>{toCurrency(netHT)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="totaux-block mt-3 px-6 no-split">
              <BlocTotaux
                sousTotalHT={hasRemise ? sousTotalHT : null}
                remise={hasRemise ? { label: remiseLabel, amountHT: remiseHT } : null}
                totalHT={netHT}
                tva={netTVA}
                totalTTC={netTTC}
              />
            </div>

            <div className="sig-block mt-4 mb-2 text-xs text-gray-700 leading-relaxed border-t pt-2 no-split">
              {mentionsLegales?.validiteDevis && <p>Validité du devis : {mentionsLegales.validiteDevis}</p>}
              {mentionsSafeHtml && <MentionsBlock html={mentionsSafeHtml} fontPx={Number(mentionsLegales?.mentions_font_px || 12)} />}
              <div className="flex justify-center gap-20 mt-8 px-4">
                <div className="text-left">
                  <p className="font-semibold text-sm">Le client</p>
                  <p className="text-xs">Mention datée et signée :</p>
                  <p className="italic text-xs">« Bon pour accord. »</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-sm">{infosSociete?.nom || "Votre société"}</p>
                </div>
              </div>
            </div>
          </div>

          {!needNewPage && (
            <div
              style={{
                height: Math.max(0, maxPageHeight - lastPageUsed - afterH - footerH - 8),
              }}
            />
          )}
          <div style={{ height: spacerH }} />
          {/* Footer collé en bas */}
          <div ref={footerRef} className="footer-apercu no-split">
            <FooterApercu />
          </div>
        </div>
      </div>
    </div>
  );
}
function ClientDisplayCard({ selectedClient, clientComplement, onOpenModal }) {
  const cardRef = useRef(null);

  const addrRaw = (selectedClient?.numero_voie || "").trim();
  const m = addrRaw.match(/^(.*?)(?:,\s*)?(\d{4,5})\s+(.+)$/i);
  const ligneAdresse1 = m ? m[1] : addrRaw;
  const cp = selectedClient?.code_postal || (m ? m[2] : "");
  const ville = (selectedClient?.ville || (m ? m[3] : "")).trim();

  return (
    <div
      ref={cardRef}
      className="absolute z-[90] top-[150px] right-[40px] min-w-[290px] max-w-[600px] bg-white border border-gray-300 shadow-md rounded p-4 text-sm"
    >
      {selectedClient ? (
        <>
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-md font-semibold">{selectedClient.nom}</h2>
            <button
              type="button"
              className="no-print text-xs text-sky-700 hover:underline"
              onClick={() => onOpenModal(cardRef.current?.getBoundingClientRect())}
            >
              Modifier
            </button>
          </div>
          <div className="text-gray-700 space-y-1 mt-1">
            {ligneAdresse1 && <p>{ligneAdresse1}</p>}
            {(cp || ville) && (
              <p>
                {cp} {ville}
              </p>
            )}
            {clientComplement?.trim() && <p>{clientComplement.trim()}</p>}
          </div>
        </>
      ) : (
        <div className="text-gray-600">
          <em>Aucun client sélectionné.</em>
          <div className="mt-2">
            <button
              type="button"
              className="no-print text-xs px-2 py-1 rounded border"
              onClick={() => onOpenModal(cardRef.current?.getBoundingClientRect())}
            >
              Choisir / créer un client
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Modal client (PORTAL) ---------- */
function ClientModal({
  clients,
  selectedClient,
  setSelectedClient,
  saisieManuelle,
  setSaisieManuelle,
  clientManuel,
  setClientManuel,
  clientComplement,
  setClientComplement,
  anchorRect,
  onClose,
}) {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState({ top: 80, left: 40 });

  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const norm = (s = "") =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

  const items = useMemo(() => {
    const all = Array.isArray(clients) ? clients : [];
    const nq = norm(q);
    if (!nq) {
      return all
        .slice()
        .sort((a, b) =>
          norm(a.nom || a.name || "").localeCompare(norm(b.nom || b.name || ""))
        )
        .slice(0, 100);
    }
    return all
      .filter((c) => norm(c.nom || c.name || "").startsWith(nq))
      .sort((a, b) =>
        norm(a.nom || a.name || "").localeCompare(norm(b.nom || b.name || ""))
      );
  }, [q, clients]);

  const onKeyDown = (e) => {
    if (!items.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(items.length - 1, i + 1));
      listRef.current?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
      listRef.current?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      const c = items[active];
      if (c) {
        setSelectedClient(c);
        onClose();
      }
    }
  };

  function highlight(text = "", query = "") {
    const t = String(text);
    const nq = norm(query);
    if (!nq) return t;
    const nt = norm(t);
    if (!nt.startsWith(nq)) return t;
    const headLen = query.length;
    return (
      <>
        <mark style={{ background: "#fff3b0" }}>{t.slice(0, headLen)}</mark>
        {t.slice(headLen)}
      </>
    );
  }

  useEffect(() => {
    const id = setTimeout(() => {
      if (!saisieManuelle) {
        inputRef.current?.focus();
      }
    }, 30);
    return () => clearTimeout(id);
  }, []); // ← pas de dépendance sur saisieManuelle

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="1"]');
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  useLayoutEffect(() => {
    const place = () => {
      const el = wrapRef.current;
      const gap = 12;
      const w = el ? el.offsetWidth : 520;
      const h = el ? el.offsetHeight : 340;

      let top = Math.max(16, anchorRect?.top ?? 140);
      let left;

      if (anchorRect) {
        left = (anchorRect.left ?? 40) - w - gap;
        if (left < 16) left = (anchorRect.right ?? 40) + gap;
      } else {
        left = Math.max(16, window.innerWidth - w - 40);
      }

      const maxTop = window.innerHeight - h - 16;
      top = Math.min(Math.max(16, top), Math.max(16, maxTop));
      setPos({ top, left });
    };

    place();
    const ro = new ResizeObserver(place);
    if (wrapRef.current) ro.observe(wrapRef.current);
    const onResize = () => place();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
  }, [anchorRect]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div className="no-print fixed inset-0 z-[1500] bg-black/20" onClick={onClose} />
      <div
        ref={wrapRef}
        className="no-print fixed z-[1600] box-border w-[520px] max-w-[92vw] max-h-[88vh] overflow-y-auto overscroll-contain"
        style={{ top: pos.top, left: pos.left }}
      >
        <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-4 max-h-[88vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Client</h3>
            <button type="button" className="text-sm px-2 py-1 rounded border" onClick={onClose}>
              Fermer
            </button>
          </div>

          {!selectedClient && (
            <p className="text-xs text-slate-500 mb-2">
              Sélectionnez un client existant ou créez-en un nouveau.
            </p>
          )}

          {/* Recherche + liste SEULEMENT quand on n'est PAS en création manuelle */}
          {!saisieManuelle && (
            <>
              {/* Barre de recherche */}
              <div className="mb-2">
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setActive(0);
                  }}
                  onKeyDown={onKeyDown}
                  placeholder="Rechercher un client… (ex: mei)"
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>

              {/* Liste de résultats */}
              <div
                ref={listRef}
                className="w-full max-h-[300px] overflow-y-auto space-y-2 outline-none"
                onKeyDown={onKeyDown}
                tabIndex={-1}
              >
                {items.length === 0 && (
                  <div className="text-xs text-slate-500 px-1">Aucun client trouvé.</div>
                )}

                {items.map((c, i) => (
                  <button
                    key={c.id || c.email || i}
                    type="button"
                    data-active={i === active ? "1" : "0"}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => {
                      setSelectedClient(c);
                      onClose();
                    }}
                    className={`block w-full text-left border rounded px-3 py-2 ${i === active ? "bg-slate-100" : "bg-white"
                      }`}
                  >
                    <div className="font-semibold">
                      {highlight(c.nom || c.name || "", q)}
                    </div>
                    <div className="text-xs text-slate-600">
                      {(c.email || c.telephone) ? (
                        <>
                          {c.email && <>{c.email} • </>}
                          {c.telephone}
                        </>
                      ) : null}
                    </div>
                    {c.adresse && (
                      <div className="text-[11px] text-slate-500">{c.adresse}</div>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Saisie manuelle / création rapide */}
          <button
            type="button"
            className="text-xs text-sky-700 hover:underline mt-3"
            onClick={() => setSaisieManuelle((v) => !v)}
          >
            {saisieManuelle ? "Annuler" : "Créer un client"}
          </button>

          {saisieManuelle && (
            <div className="mt-2">
              <ClientForm
                clientManuel={clientManuel}
                setClientManuel={setClientManuel}
                setSelectedClient={(c) => {
                  setSelectedClient(c);      // sélectionne immédiatement le nouveau client
                  setSaisieManuelle(false);  // referme le formulaire après création
                }}
              />
            </div>
          )}

          {/* Complément d’adresse */}
          <div className="mt-3">
            <label className="block text-sm font-medium mb-1">
              Précision adresse (optionnel)
            </label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder="Ex : Bât. B – 3e étage – Digicode 12A"
              value={clientComplement}
              onChange={(e) => setClientComplement(e.target.value)}
            />
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="px-3 py-1 rounded border" onClick={onClose}>
              OK
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------- Formulaire création client ---------- */
function ClientForm({ clientManuel, setClientManuel, setSelectedClient }) {
  const [saving, setSaving] = useState(false);

  // petit helper pour éviter tout bug de state "écrasé"
  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setClientManuel((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="space-y-2 text-sm">
      <input
        type="text"
        placeholder="Nom"
        className="border p-1 rounded w-full"
        value={clientManuel.nom}
        onChange={handleChange("nom")}
      />
      <input
        type="text"
        placeholder="Adresse"
        className="border p-1 rounded w-full"
        value={clientManuel.numeroVoie}
        onChange={handleChange("numeroVoie")}
      />
      <input
        type="text"
        placeholder="Code postal"
        className="border p-1 rounded w-full"
        value={clientManuel.codePostal}
        onChange={handleChange("codePostal")}
      />
      <input
        type="text"
        placeholder="Ville"
        className="border p-1 rounded w-full"
        value={clientManuel.ville}
        onChange={handleChange("ville")}
      />
      <input
        type="text"
        placeholder="Téléphone"
        className="border p-1 rounded w-full"
        value={clientManuel.telephone}
        onChange={handleChange("telephone")}
      />
      <input
        type="email"
        placeholder="Email"
        className="border p-1 rounded w-full"
        value={clientManuel.email}
        onChange={handleChange("email")}
      />
      <select
        className="border p-1 rounded w-full"
        value={clientManuel.type}
        onChange={handleChange("type")}
      >
        <option value="particulier">Particulier</option>
        <option value="pro">Professionnel</option>
      </select>

      <button
        disabled={saving}
        onClick={async () => {
          if (!clientManuel.nom.trim()) {
            notify.warning({
              title: "Nom manquant",
              message: "Le nom du client est obligatoire.",
            });
            return;
          }
          try {
            setSaving(true);
            const res = await axiosInstance.post("/clients", {
              nom: clientManuel.nom,
              numero_voie: clientManuel.numeroVoie,
              code_postal: clientManuel.codePostal,
              ville: clientManuel.ville,
              telephone: clientManuel.telephone,
              email: clientManuel.email,
              type: clientManuel.type,
            });
            const nouveauClient = res.data;

            // sélectionne immédiatement ce nouveau client dans CreerDevis
            setSelectedClient(nouveauClient);

            notify.success({
              title: "Client créé",
              message: "Le client a été ajouté.",
            });
          } catch (error) {
            console.error("Erreur création client :", error);
            notify.error({
              title: "Échec création client",
              message: "Impossible d’ajouter le client.",
            });
          } finally {
            setSaving(false);
          }
        }}
        className="bg-sky-700 hover:bg-sky-600 text-white px-3 py-1 rounded mt-2"
      >
        {saving ? "Enregistrement…" : "Enregistrer le client"}
      </button>
    </div>
  );
}
